import { getMapsRegion } from '@rsos/utils/googleMapsRegion';
import {
  getGoogleMapsApiKey,
  getGoogleMapsApiVersion,
} from '@rsos/utils/metaTags';
import { MapsNotReadyError } from '../../interface/reverseGeocode/googleGeocoderReady';
import './types';

/** The Maps JS libraries this codebase pulls in. */
export type MapsLibrary = 'maps' | 'marker' | 'elevation' | 'geocoding';

export interface LoadGoogleMapsOptions {
  /** Defaults to the `google-maps-key` meta tag. */
  apiKey?: string;
  /**
   * Agency alpha-2, for country-specific place names. Only the first caller's
   * value is used, since the region is fixed on the loader URL. React callers
   * should pass the redux value; everything else gets the configured default.
   */
  region?: string;
}

const SCRIPT_ID = 'google-maps-api';
const POLL_INTERVAL_MS = 100;
const READY_TIMEOUT_MS = 10000;

let bootstrapPromise: Promise<typeof google.maps> | null = null;

/**
 * The `google.maps` namespace exists before its members are registered, and the
 * types declare them all as required — so check the function the way
 * isGoogleGeocoderReady does, or TS insists the check is redundant.
 */
const readyMaps = () => {
  const maps = window.google?.maps;
  return typeof maps?.importLibrary === 'function' ? maps : undefined;
};

const mapsScriptExists = () =>
  !!document.getElementById(SCRIPT_ID) ||
  !!document.querySelector('script[src*="maps.googleapis.com"]') ||
  !!window.alreadyRequestedGoogleMaps;

/**
 * Resolves once `google.maps.importLibrary` exists, which is the earliest point
 * a library can be requested. Deliberately not the script's `load` event: that
 * fires when the bootstrap has downloaded, which says nothing about whether any
 * given constructor is registered yet.
 */
const whenBootstrapReady = () =>
  new Promise<typeof google.maps>((resolve, reject) => {
    const startedAt = Date.now();

    const check = () => {
      const maps = readyMaps();
      if (maps) {
        resolve(maps);
        return;
      }
      if (Date.now() - startedAt > READY_TIMEOUT_MS) {
        reject(
          new MapsNotReadyError(
            'Timed out waiting for the Google Maps JavaScript API to load.',
          ),
        );
        return;
      }
      setTimeout(check, POLL_INTERVAL_MS);
    };

    check();
  });

/**
 * Injects the tag and returns a promise that only ever rejects, so callers can
 * race it against readiness and fail immediately on a network error instead of
 * waiting out the timeout.
 */
const injectMapsScript = (apiKey: string, region: string) =>
  new Promise<never>((_resolve, reject) => {
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.type = 'text/javascript';
    script.async = true;
    // `loading=async` is what provides importLibrary, so every caller has to
    // get it. That is why injection lives here rather than per-caller.
    script.src =
      `https://maps.googleapis.com/maps/api/js?key=${apiKey}` +
      `&loading=async&libraries=marker` +
      `&v=${getGoogleMapsApiVersion() || 'quarterly'}&region=${region}`;

    script.onerror = () =>
      reject(
        new MapsNotReadyError(
          'The Google Maps JavaScript API script failed to load.',
        ),
      );

    window.alreadyRequestedGoogleMaps = true;
    document.head.appendChild(script);
  });

/**
 * Removes the failed tag and the guard flag so a later attempt re-injects.
 * Without this, every retry skips injection and polls a script that will never
 * resolve.
 */
const clearFailedLoad = () => {
  document.getElementById(SCRIPT_ID)?.remove();
  delete window.alreadyRequestedGoogleMaps;
  bootstrapPromise = null;
};

const bootstrapGoogleMaps = (options: LoadGoogleMapsOptions) => {
  if (bootstrapPromise) return bootstrapPromise;

  bootstrapPromise = (async () => {
    const alreadyReady = readyMaps();
    if (alreadyReady) {
      return alreadyReady;
    }

    if (mapsScriptExists()) {
      return whenBootstrapReady();
    }

    const apiKey = options.apiKey || getGoogleMapsApiKey();
    if (!apiKey) {
      throw new MapsNotReadyError('No Google Maps API key is configured.');
    }

    const scriptFailed = injectMapsScript(
      apiKey,
      options.region || getMapsRegion(),
    );

    return Promise.race([whenBootstrapReady(), scriptFailed]);
  })();

  // Don't cache a rejection for the rest of the session. The original
  // rejection still reaches this caller.
  bootstrapPromise.catch(clearFailedLoad);

  return bootstrapPromise;
};

/**
 * Loads the Google Maps JS API at most once per page and resolves when
 * `libraries` are actually usable.
 */
export async function loadGoogleMaps(
  libraries: MapsLibrary | MapsLibrary[],
  options: LoadGoogleMapsOptions = {},
): Promise<typeof google.maps> {
  const maps = await bootstrapGoogleMaps(options);
  const names = Array.isArray(libraries) ? libraries : [libraries];

  await Promise.all(names.map(name => maps.importLibrary(name)));

  return window.google?.maps || maps;
}
